import queryString from 'query-string';
import { generatePath as generateRouterPath } from 'react-router';

import { PARTS, SALES, TECHNICIAN } from 'constants/ModeConstants';
import { PATH } from 'constants/RoutesConstants';

import NAVIGATION_SECTIONS from 'ui/layout/NavigationBar/config';
import { getClassName } from 'ui/utils/ModuleInfo';
import { uniq } from 'utils/ObjectUtils';

/**
 * @description Generate a new path having the notificationId
 * @param {string} notificationId
 * @param {string} path
 * @param {Object} params
 * @returns {string} The new path
 */
export const generatePath = (notificationId, path, params) =>
  queryString.stringifyUrl({ url: generateRouterPath(path, params), query: { notificationId } });

const REDIRECT_PATHS = {
  [SALES]: PATH.SALES_DETAILED.path,
  [PARTS]: PATH.PARTS_DETAILED.path,
  [TECHNICIAN]: PATH.AFTERSALES_DETAILED.path,
};

/**
 * @param {import('modules/notifications/types').ConversationNotification} notification
 * @param {UINotificationSetting} setting
 * @return {UINotificationSetting}
 */
export const overrideSettingForLinkedConversation = ({ id, conversation }, setting) => {
  const { latestMessage } = conversation;
  const { intervention } = latestMessage;
  const appointmentId = intervention?.appointmentId;

  const isAppointmentMsg = typeof appointmentId !== 'undefined';
  const isInterventionMsg = typeof intervention !== 'undefined' && !isAppointmentMsg;
  const isLinkedMessage = isAppointmentMsg || isInterventionMsg;

  if (isLinkedMessage) {
    const linkedEntity = isAppointmentMsg ? 'APPOINTMENT' : 'INTERVENTION';

    return {
      ...setting,
      title: `CHAT.${linkedEntity}_MESSAGE`,
      badge: isAppointmentMsg ? 'fb-icon--calendar' : getClassName(intervention.module),
      linkReference: {
        title: intervention.referenceId,
        path: isAppointmentMsg
          ? generatePath(id, PATH.KEY_DROP_OFF_APPOINTMENTS_DETAILED.path, { appointmentId })
          : generatePath(id, REDIRECT_PATHS[intervention.mode], {
            interventionId: intervention.id,
          }),
      },
    };
  }
  return setting;
};

/**
 * @param {Partial<import('modules/notifications/types').NotificationConfiguration>} config
 * @return {import("modules/global/typing/enums/extensions.enum").ExtensionEnum[]}
 */
export const getAvailableNotificationExtension = (config = {}) =>
  NAVIGATION_SECTIONS.reduce((acc, { extension, children = [] }) => {
    const extensions = Array.isArray(extension) ? extension : [extension];
    const childrenExtensions = children.flatMap(({ extension: childrenExtension }) => childrenExtension);
    const sectionExtensions = uniq([...extensions, ...childrenExtensions].filter(Boolean));
    return [...acc, ...sectionExtensions.filter(sectionExtension => Object.keys(config).includes(sectionExtension))];
  }, []);
